// Multi-factor authentication (TOTP) API — connected to the auth service via
// the API gateway.
//
// Enrollment is deliberately two-step on the backend: startEnrollment() issues
// a secret but leaves MFA OFF, and only confirmEnrollment() — which requires a
// working code from the user's authenticator — switches it on. Never present
// this to the user as a single "enable" action: if their QR scan silently
// failed, a one-step enable would lock them out of their own account.
//
// Backend: services/auth/main.py (/auth/mfa/*), services/auth/test_mfa*.py.

import { authenticatedFetch } from './authenticatedFetch';

export interface MFAStatus {
  enabled: boolean;
  /** A secret exists but was never confirmed — enrollment was abandoned. */
  enrollment_pending: boolean;
  backup_codes_remaining: number;
  last_used_at: string | null;
}

export interface MFAEnrollment {
  /** Shown so a user who cannot scan the QR image can type it in instead. */
  secret: string;
  otpauth_uri: string;
  /** Base64-encoded PNG, without the data: prefix. */
  qr_code: string;
}

export interface MFABackupCodes {
  backup_codes: string[];
}

async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  return authenticatedFetch<T>(endpoint, { ...options, serviceName: 'MFA API' });
}

/**
 * True when the backend is asking for a second factor rather than rejecting
 * the credentials.
 *
 * `POST /auth/login` answers a code-less login from an MFA-enabled account with
 * ValueError("MFA code required") -> 401 — the same status as a wrong password.
 * Without this distinction the login form tells a user with perfectly correct
 * credentials that they are invalid.
 */
function isMfaRequiredError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return message.toLowerCase().includes('mfa code required');
}

export const mfaAPI = {
  status: () => apiRequest<MFAStatus>('/auth/mfa/status', { method: 'GET' }),

  /** Issues a TOTP secret. Does NOT enable MFA — confirmEnrollment does. */
  startEnrollment: () => apiRequest<MFAEnrollment>('/auth/mfa/enroll', { method: 'POST' }),

  /** Proves the authenticator works, enables MFA, and returns backup codes.
   *  This is the only response that ever carries plaintext backup codes. */
  confirmEnrollment: (code: string) =>
    apiRequest<MFABackupCodes>('/auth/mfa/enroll/confirm', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  /** Requires the account password AND a current factor. */
  disable: (password: string, code: string) =>
    apiRequest<{ success: boolean; message: string }>('/auth/mfa/disable', {
      method: 'POST',
      body: JSON.stringify({ password, code }),
    }),

  /** Invalidates every previous backup code. */
  regenerateBackupCodes: (code: string) =>
    apiRequest<MFABackupCodes>('/auth/mfa/backup-codes', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  isMfaRequiredError,
};
